/**
 * TypeScript types for WebSocket communication in FlexHelp
 * These types are shared between client and server for type safety
 */

// Message type enum for WebSocket messages
export enum WebSocketMessageTypes {
  NOTIFICATION = 'notification',
  CHAT = 'chat',
  STATUS_UPDATE = 'status_update',
  PRESENCE = 'presence',
  ERROR = 'error',
  PING = 'ping',
  PONG = 'pong',
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  TYPING = 'typing',
  READ = 'read',
  TASK_UPDATE = 'task_update',
  LOCATION_UPDATE = 'location_update'
}

// Base WebSocket message interface
export interface WebSocketMessage {
  type: string | WebSocketMessageTypes;
  payload?: unknown;
  userId?: string;
  preferences?: Record<string, unknown>;
  notification?: unknown;
  message?: unknown;
  conversationId?: string;
  taskId?: string;
  data?: unknown;
  update?: unknown;
  status?: unknown;
  timestamp?: string | Date;
}

// Notification message
export interface NotificationMessage extends WebSocketMessage {
  type: WebSocketMessageTypes.NOTIFICATION | 'notification';
  payload: {
    id: string;
    message: string;
    timestamp: string;
    read: boolean;
    actionUrl?: string;
  };
}

// Chat message - renamed to WebSocketChatMessage to avoid conflict with chat.ts
export interface WebSocketChatMessage extends WebSocketMessage {
  type: WebSocketMessageTypes.CHAT | 'chat';
  payload: {
    id: string;
    content: string;
    senderId: string;
    timestamp: string;
    chatId: string;
  };
}

// Status update message
export interface StatusUpdateMessage extends WebSocketMessage {
  type: WebSocketMessageTypes.STATUS_UPDATE | 'status_update';
  payload: {
    entityId: string;
    entityType: string;
    oldStatus: string;
    newStatus: string;
    timestamp: string;
  };
}

// User presence message
export interface UserPresenceMessage extends WebSocketMessage {
  type: WebSocketMessageTypes.PRESENCE | 'presence';
  payload: {
    userId: string;
    status: 'online' | 'offline' | 'away';
    lastSeen?: string;
  };
}

// Error message
export interface ErrorMessage extends WebSocketMessage {
  type: WebSocketMessageTypes.ERROR | 'error';
  payload: {
    code: string;
    message: string;
  };
}

// Type for use in notification service
export interface SocketMessageData {
  type: string | WebSocketMessageTypes;
  notification?: unknown;
  message?: unknown;
  conversationId?: string;
  preferences?: Record<string, unknown>;
  [key: string]: unknown;
}

// WebSocket connection status
export enum WebSocketConnectionStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed'
}

// WebSocket configuration
export interface WebSocketConfig {
  reconnectAttempts: number;
  reconnectInterval: number;
  heartbeatInterval: number;
}
